/**
 * Reading an environment name that came from configuration.
 *
 * Its own file rather than a few more lines in `paths.ts`, for one reason: `paths.ts` exports
 * `ApiPaths`, which is a TypeScript `enum`, and the tests run under `node --test`, which strips
 * types rather than compiling them and refuses an enum outright. A test that cannot import the
 * module cannot be written, and this is the one function here whose *failure* behaviour is the
 * part worth asserting.
 *
 * The type import below is erased at runtime, so nothing in this file loads the enum.
 */

import type { DictarisEnvironment } from './paths.ts';

/**
 * The environment a client was *told* it is in, rather than one it worked out for itself.
 *
 * The web client cannot use `resolveEnvironment` in `paths.ts`: it has no `isPackaged` and no
 * release channel. It is a static bundle that a container hands to a browser, and the only
 * thing that
 * knows whether it is `test.web.dictaris.ro` or `web.dictaris.ro` is the deployment that started
 * the container. The answer therefore arrives as an unvalidated string.
 *
 * An unrecognised one throws rather than falling back, because both available fallbacks are
 * wrong in a way nobody notices: defaulting to `test` points production at the test API, and
 * defaulting to `production` points a test deployment at real patient data. A typo in a compose
 * file should stop the app on its first load, where it is one line in a log, rather than
 * surface a week later as a report filed against the wrong database.
 */
export function parseEnvironment(value: string | null | undefined): DictarisEnvironment {
  if (value === 'production' || value === 'demo' || value === 'test' || value === 'local') return value;

  throw new Error(`Unknown Dictaris environment ${JSON.stringify(value)}. Expected one of: production, demo, test, local.`);
}
